import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { selectedCustomerIdSelector } from '@store/common/selectors';
import { getCustomerDefaultLandingPage } from '@api/landingPage';

interface CustomerDefaultLandingPageInfo {
  url: string;
  name: string;
  path: string;
  exists: boolean;
}

/**
 * Fetches customer-level default landing page information
 * This is used by admins to see which dashboard is set as default for all users
 */
const fetchCustomerDefaultLandingPage = async (): Promise<CustomerDefaultLandingPageInfo> => {
  try {
    const data = await getCustomerDefaultLandingPage();
    const { configuration, exists } = data;

    if (exists && configuration) {
      return {
        url: configuration.url || '',
        name: configuration.name || '',
        path: configuration.path || '',
        exists: true,
      };
    }

    return {
      url: '',
      name: '',
      path: '',
      exists: false,
    };
  } catch (error) {
    // If 404 or no data, return empty state
    return {
      url: '',
      name: '',
      path: '',
      exists: false,
    };
  }
};

/**
 * Hook to get customer-level default landing page info
 * Used by admin users to view and manage the default landing page for all users
 */
export const useCustomerDefaultLandingPageInfo = () => {
  const customerId = useSelector(selectedCustomerIdSelector);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['customerDefaultLandingPage', customerId],
    queryFn: fetchCustomerDefaultLandingPage,
    enabled: !!customerId,
    staleTime: 0, // Always refetch to ensure fresh data when popup opens
    refetchOnWindowFocus: false,
  });

  return {
    customerDefaultInfo: data,
    isLoading,
    error,
    refetch,
  };
};
